const ALPHA_VISIBLE_THRESHOLD = 8;

export type TransparentImageTrim = {
  top: number;
  right: number;
  bottom: number;
  left: number;
  sourceWidth: number;
  sourceHeight: number;
};

/**
 * Finds the visible pixel bounds in image data.
 *
 * S&S source images can contain transparent padding around the actual card art.
 * The returned trim lets CardPreview hide that padding without modifying the
 * generated image data.
 *
 * @param imageData Pixel data for a preview image
 * @returns Transparent edge trim, or undefined when no trim is needed
 */
export const getTransparentImageTrimFromImageData = (imageData: ImageData): TransparentImageTrim | undefined => {
  let minX = imageData.width;
  let minY = imageData.height;
  let maxX = -1;
  let maxY = -1;

  for (let y = 0; y < imageData.height; y += 1) {
    for (let x = 0; x < imageData.width; x += 1) {
      const alphaIndex = (y * imageData.width + x) * 4 + 3;
      const alpha = imageData.data.at(alphaIndex) ?? 0;

      if (alpha <= ALPHA_VISIBLE_THRESHOLD) {
        continue;
      }

      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }
  }

  if (maxX < 0 || maxY < 0) {
    return undefined;
  }

  const trim = {
    top: minY,
    right: imageData.width - 1 - maxX,
    bottom: imageData.height - 1 - maxY,
    left: minX,
    sourceWidth: imageData.width,
    sourceHeight: imageData.height,
  };

  if (trim.top === 0 && trim.right === 0 && trim.bottom === 0 && trim.left === 0) {
    return undefined;
  }

  return trim;
};

/**
 * Calculates transparent edge trim for a loaded image element.
 *
 * @param image Loaded preview image element
 * @returns Transparent edge trim, or undefined when no trim can be computed
 */
export const getTransparentImageTrim = (image: HTMLImageElement): TransparentImageTrim | undefined => {
  const width = image.naturalWidth || image.width;
  const height = image.naturalHeight || image.height;

  if (!width || !height) {
    return undefined;
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) {
    return undefined;
  }

  context.drawImage(image, 0, 0);
  return getTransparentImageTrimFromImageData(context.getImageData(0, 0, width, height));
};
